import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { MapPin, Navigation, Phone, ShieldAlert } from "lucide-react";
import { ResponderApi } from "../../api/services";
import { errorMessage } from "../../api/client";
import { external, location, GeoPoint } from "../../platform";
import { PageHeader, PriorityBadge, Spinner, StatusBadge, EmptyState } from "../../components/common";
import { Switch } from "../../components/ui/switch";
import { MapView, MapMarker } from "../../maps/MapView";
import { useRoute } from "../../maps/useRoute";
import { TripProgress } from "../../components/TripTimeline";
import { ago, kmBetween, phone } from "../../lib/format";

const NEXT: Record<string, { status: string; label: string }> = {
  accepted: { status: "en_route", label: "Start trip — en route to patient" },
  en_route: { status: "at_pickup", label: "Arrived at pickup" },
  at_pickup: { status: "in_transit", label: "Confirm pickup — patient on board" },
  in_transit: { status: "completed", label: "Complete service" },
};

function useLocationShare() {
  const [state, setState] = useState<"off" | "sharing" | "error">("off");
  const [err, setErr] = useState("");
  const [pos, setPos] = useState<GeoPoint | null>(null);
  const stopRef = useRef<(() => void) | undefined>(undefined);
  const lastSent = useRef(0);

  const start = () => {
    setErr("");
    stopRef.current?.();
    stopRef.current = location.watchPosition((p) => {
      setState("sharing"); setPos(p);
      if (Date.now() - lastSent.current > 5000) {
        lastSent.current = Date.now();
        ResponderApi.sendLocation(p).catch(() => {});
      }
    }, (e) => { setState("error"); setErr(e.message); });
  };
  const stop = () => { stopRef.current?.(); stopRef.current = undefined; setState("off"); };
  useEffect(() => () => stopRef.current?.(), []);
  return { state, err, pos, start, stop };
}

function LocationCard({ share }: { share: ReturnType<typeof useLocationShare> }) {
  return (
    <div className="fp-card space-y-2" data-testid="responder-location-card">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="font-semibold text-slate-900">Location sharing</p>
          <p className="text-sm text-slate-600" data-testid="responder-location-status">
            {share.state === "sharing" ? `Sharing · ±${Math.round(share.pos?.accuracy || 0)} m` : share.state === "error" ? share.err : "Not sharing"}
          </p>
        </div>
        {share.state === "sharing"
          ? <button className="fp-btn-secondary" onClick={share.stop} data-testid="responder-location-stop">Stop</button>
          : <button className="fp-btn-primary" onClick={share.start} data-testid="responder-location-share"><MapPin size={16} /> Share my location</button>}
      </div>
      <p className="text-xs text-slate-500">Your browser asks permission first. Location is sent only while FASTPASS is open on screen — background GPS tracking is not available in the web app.</p>
    </div>
  );
}

function Incoming({ b }: { b: any }) {
  const qc = useQueryClient();
  const done = () => qc.invalidateQueries({ queryKey: ["responder"] });
  const accept = useMutation({ mutationFn: () => ResponderApi.accept(b.id), onSuccess: done, onError: (e) => toast.error(errorMessage(e)) });
  const reject = useMutation({ mutationFn: (reason: string) => ResponderApi.reject(b.id, reason), onSuccess: done, onError: (e) => toast.error(errorMessage(e)) });
  return (
    <div className="fp-card space-y-3 border-2 border-[#FBBF2E]" data-testid="incoming-request-card">
      <div className="flex items-center justify-between"><p className="font-display text-lg font-bold">New assignment · {b.code}</p><PriorityBadge p={b.priority} /></div>
      <p className="text-sm text-slate-700"><strong>{b.type === "emergency" ? "SOS" : b.type === "ptv" ? "PTV" : "Scheduled"}:</strong> {b.condition || "—"}</p>
      <p className="text-sm text-slate-700"><strong>Pickup:</strong> {b.pickup.address || `${b.pickup.lat.toFixed(5)}, ${b.pickup.lng.toFixed(5)}`}</p>
      <p className="text-sm text-slate-700"><strong>Patient:</strong> {b.patient?.name} · {phone(b.contact_phone)}</p>
      <div className="grid grid-cols-2 gap-2">
        <button className="fp-btn-secondary" disabled={reject.isPending} onClick={() => { const r = window.prompt("Reason for rejecting?") ; if (r !== null) reject.mutate(r); }} data-testid="incoming-reject-button">Reject</button>
        <button className="fp-btn-primary" disabled={accept.isPending} onClick={() => accept.mutate()} data-testid="incoming-accept-button">Accept</button>
      </div>
    </div>
  );
}

function ActiveTrip({ b, me }: { b: any; me: GeoPoint | null }) {
  const qc = useQueryClient();
  const step = NEXT[b.status];
  const target = b.status === "in_transit" && b.destination ? b.destination : b.pickup;
  const { route, routed } = useRoute(me, target);
  const adv = useMutation({
    mutationFn: () => ResponderApi.status(b.id, step.status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["responder"] }), onError: (e) => toast.error(errorMessage(e)),
  });
  const markers: MapMarker[] = [{ id: "p", lat: b.pickup.lat, lng: b.pickup.lng, kind: "pickup", label: "Patient pickup" }];
  if (b.destination) markers.push({ id: "d", lat: b.destination.lat, lng: b.destination.lng, kind: "destination", label: "Destination" });
  if (me) markers.push({ id: "me", lat: me.lat, lng: me.lng, kind: "me", label: "You" });
  const km = kmBetween(me, target);
  return (
    <div className="space-y-4" data-testid="active-trip">
      <div className="fp-card space-y-3">
        <div className="flex items-center justify-between"><p className="font-display text-lg font-bold">Active trip · {b.code}</p><StatusBadge status={b.status} label={b.status_label} testId="active-trip-status" /></div>
        <TripProgress status={b.status} />
      </div>
      <MapView className="h-72" markers={markers} route={me ? route : undefined} routeDashed={!routed} testId="responder-trip-map" />
      <div className="fp-card space-y-2 text-sm">
        <p><strong>Patient:</strong> {b.patient?.name} {km != null && <span className="text-slate-500">· {km.toFixed(1)} km away</span>}</p>
        <p><strong>{b.status === "in_transit" ? "Drop-off" : "Pickup"}:</strong> {target.address || `${target.lat.toFixed(5)}, ${target.lng.toFixed(5)}`}</p>
        {b.condition && <p><strong>Condition:</strong> {b.condition}</p>}
        {b.notes && <p><strong>Notes:</strong> {b.notes}</p>}
        {b.patient?.medical_notes && <p><strong>Medical notes:</strong> {b.patient.medical_notes}</p>}
        <div className="grid grid-cols-2 gap-2 pt-2">
          <button className="fp-btn-secondary" onClick={() => external.navigateTo(target.lat, target.lng)} data-testid="trip-navigate-button"><Navigation size={16} /> Navigate</button>
          <a className="fp-btn-secondary" href={`tel:${b.contact_phone}`} data-testid="trip-call-patient"><Phone size={16} /> Call patient</a>
        </div>
      </div>
      {step && (
        <button className="fp-btn-primary h-14 w-full text-base" disabled={adv.isPending} onClick={() => adv.mutate()} data-testid="trip-advance-button">{step.label}</button>
      )}
    </div>
  );
}

export default function ResponderHome() {
  const qc = useQueryClient();
  const share = useLocationShare();
  const { data: me, isLoading } = useQuery({ queryKey: ["responder", "me"], queryFn: ResponderApi.me });
  const verified = me?.verification?.status === "verified" && me?.status === "active";
  const { data: trips } = useQuery({ queryKey: ["responder", "assignments"], queryFn: ResponderApi.assignments, enabled: !!verified, refetchInterval: 30000 });
  const avail = useMutation({
    mutationFn: (on: boolean) => ResponderApi.setAvailability(on ? "available" : "offline"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["responder"] }), onError: (e) => toast.error(errorMessage(e)),
  });
  if (isLoading || !me) return <Spinner />;

  if (!verified) {
    return (
      <div className="space-y-4">
        <PageHeader title="Responder" />
        <div className="fp-card flex gap-3 border-amber-300 bg-amber-50" data-testid="responder-pending-verification">
          <ShieldAlert className="shrink-0 text-amber-700" />
          <div><p className="font-semibold text-amber-900">Verification {me.verification?.status === "rejected" ? "rejected" : "pending"}</p>
            <p className="text-sm text-amber-900">An administrator must verify your identity and credentials before you can go on duty. You will not receive dispatch assignments until then.</p>
            {me.verification?.notes && <p className="mt-1 text-sm">Note: {me.verification.notes}</p>}</div>
        </div>
      </div>
    );
  }
  const incoming = (trips || []).filter((t) => t.status === "assigned");
  const active = (trips || []).find((t) => t.status !== "assigned");
  return (
    <div className="space-y-5">
      <PageHeader title="On duty" subtitle={me.vehicle ? `${me.vehicle.type} · ${me.vehicle.plate}` : "No vehicle assigned"} />
      <div className="fp-card flex items-center justify-between gap-3" data-testid="responder-availability-card">
        <div>
          <p className="font-semibold text-slate-900">Availability</p>
          <p className="text-sm text-slate-600"><StatusBadge status={me.availability || "offline"} testId="responder-availability-status" /> {me.availability_at && <span className="text-xs text-slate-400">since {ago(me.availability_at)}</span>}</p>
        </div>
        <Switch checked={me.availability !== "offline"} disabled={me.availability === "busy" || avail.isPending}
          onCheckedChange={(v: boolean) => avail.mutate(v)} data-testid="responder-availability-toggle" aria-label="Toggle availability" />
      </div>
      <LocationCard share={share} />
      {incoming.map((b) => <Incoming key={b.id} b={b} />)}
      {active ? <ActiveTrip b={active} me={share.pos} /> : !incoming.length && <EmptyState title="No active assignment" body={me.availability === "available" ? "Stay on this screen — new dispatches appear instantly." : "Go available to receive dispatches."} testId="responder-no-assignment" />}
    </div>
  );
}
